import { BeneficiaryModifiedDiscrepancy, ChangedBeneficiary, DeletedBeneficiary, Reference } from '@approvalmax/types';
import { openSplitViewState } from '@approvalmax/ui/src/components';
import { useAtom, useSetAtom } from 'jotai';
import cloneDeep from 'lodash/cloneDeep';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getAirwallexContext } from 'pages/requestForm/selectors/airwallex/airwallexSelectors';
import {
    getCanCreateBeneficiary,
    getFieldsApprovalPermissionBySystemPurpose,
    getRequestEditMode,
} from 'pages/requestForm/selectors/requestSelectors';
import React, { FC, useCallback, useContext, useMemo, useState } from 'react';
import { notificationService } from 'services/notification';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { useSaveAndSubmitMutation } from './XeroAirwallexBatchPaymentForm.hooks';
import { messages } from './XeroAirwallexBatchPaymentForm.messages';
import {
    CheckBeneficiaryResult,
    XeroAirwallexBatchPaymentFormContextProviderProps,
    XeroAirwallexBatchPaymentFormContextType,
} from './XeroAirwallexBatchPaymentForm.types';
import { getInitialCheckBeneficiaryResultsByInvoiceId } from './XeroAirwallexBatchPaymentForm.utils';

const XeroAirwallexBatchPaymentFormContext = React.createContext<XeroAirwallexBatchPaymentFormContextType>(null as any);

export const XeroAirwallexBatchPaymentFormContextProvider: FC<XeroAirwallexBatchPaymentFormContextProviderProps> = (
    props
) => {
    const { children, editRequestField, request } = props;

    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const [openSplitView, setOpenSplitView] = useAtom(openSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);

    const [billBatchPayment, setBillBatchPayment] = useState<domain.XeroAirwallexBatchPayment>(
        (cloneDeep(request.details) as domain.XeroAirwallexBatchPaymentDetails) || {
            items: [],
        }
    );
    const [showErrors, setShowErrors] = useState(false);
    const [billInvoiceRequestIdsWithExceededAmount, setBillInvoiceRequestIdsWithExceededAmount] = useState<string[]>(
        []
    );
    const [billInvoiceRequestIdsWithError, setBillInvoiceRequestIdsWithError] = useState<string[]>([]);
    const airwallexContext = useSelector(getAirwallexContext);
    const [checkBeneficiaryResultsByInvoiceId, setCheckBeneficiaryResultsByInvoiceId] = useState<
        Record<string, CheckBeneficiaryResult>
    >(getInitialCheckBeneficiaryResultsByInvoiceId(billBatchPayment));
    const [deletedBeneficiariesData, setDeletedBeneficiariesData] = useState<DeletedBeneficiary[] | null>(null);
    const [changedBeneficiariesData, setChangedBeneficiariesData] = useState<BeneficiaryModifiedDiscrepancy[] | null>(
        null
    );
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const canCreateBeneficiary = useSelector((state) => getCanCreateBeneficiary(state, request));
    const [removeLinesPermission] = useSelector((state) =>
        getFieldsApprovalPermissionBySystemPurpose(state, request, [domain.FieldSystemPurpose.XeroBatchPaymentItems])
    );
    const removeLinesDisabled = removeLinesPermission.disabled;

    const sourceCurrencies = useMemo(
        () => airwallexContext.currencies.map((c) => ({ id: c, text: c })),
        [airwallexContext.currencies]
    );

    const removeSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            const newItems = billBatchPayment.items.filter(
                (bill) => bill.xeroBillInvoiceRequestId !== xeroBillInvoiceRequestId
            );

            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));

            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);

            setCheckBeneficiaryResultsByInvoiceId((prevState) => {
                const newState = { ...prevState };

                delete newState[xeroBillInvoiceRequestId];

                return newState;
            });
        },
        [billBatchPayment.items, editRequestField]
    );

    const changeBillInvoiceItemField = useCallback(
        (
            xeroBillInvoiceRequestId: string,
            itemField: keyof domain.AirwallexPaymentItem,
            newValue: domain.AirwallexPaymentItem[keyof domain.AirwallexPaymentItem]
        ) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                [itemField]: bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? newValue : bill[itemField],
            }));

            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));
        },
        [billBatchPayment.items, editRequestField]
    );

    const changePaymentMethod = useCallback(
        (xeroBillInvoiceRequestId: string, paymentMethod: Reference) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                paymentMethod:
                    bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId
                        ? paymentMethod
                        : bill.beneficiary?.paymentMethod,
                paymentFee: bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? null : bill.paymentFee,
            }));

            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));
        },
        [billBatchPayment.items, editRequestField]
    );

    const changeBankAccount = useCallback(
        (xeroBillInvoiceRequestId: string, bankAccount: Reference | null, sourceCurrency: Reference | null) => {
            const newItems = billBatchPayment.items.map((bill) => ({
                ...bill,
                bankAccount:
                    bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? bankAccount : bill.bankAccount,
                sourceCurrency:
                    bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId && sourceCurrency
                        ? sourceCurrency
                        : bill.sourceCurrency,
            }));

            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));
        },
        [billBatchPayment.items, editRequestField]
    );

    const changeBeneficiary = useCallback(
        (xeroBillInvoiceRequestId: string, beneficiary: domain.AirwallexBeneficiary | null) => {
            const beneficiaryPaymentMethod = beneficiary?.paymentMethod || null;

            const newItems = billBatchPayment.items.map((bill) => {
                const billCurrencyExistsInSourceCurrencyList = !!sourceCurrencies.find(
                    (item) => item.id === bill.currency
                );

                let sourceCurrency: Reference | null = null;

                if (bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId) {
                    if (billCurrencyExistsInSourceCurrencyList) {
                        sourceCurrency =
                            beneficiary && bill.currency !== beneficiary.accountCurrency
                                ? { id: bill.currency, text: bill.currency }
                                : bill.sourceCurrency;
                    } else {
                        if (bill.currency === beneficiary?.accountCurrency) {
                            sourceCurrency = { id: request.companyCurrency, text: request.companyCurrency };
                        } else {
                            notificationService.showErrorToast(messages.noAvailableSourceCurrency);
                        }
                    }
                } else {
                    sourceCurrency = bill.sourceCurrency;
                }

                return {
                    ...bill,
                    beneficiary:
                        bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId ? beneficiary : bill.beneficiary,
                    paymentMethod:
                        bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId
                            ? beneficiaryPaymentMethod
                            : bill.beneficiary?.paymentMethod,
                    paymentFee:
                        bill.xeroBillInvoiceRequestId === xeroBillInvoiceRequestId &&
                        beneficiaryPaymentMethod?.id === domain.AirwallexPaymentMethod.Local
                            ? null
                            : bill.paymentFee,
                    sourceCurrency,
                };
            });

            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);
            setBillBatchPayment((prevBillBatchPayment) => ({
                ...prevBillBatchPayment,
                items: newItems,
            }));

            setCheckBeneficiaryResultsByInvoiceId((prevState) => {
                const newState = { ...prevState };

                delete newState[xeroBillInvoiceRequestId];

                return newState;
            });
        },
        [billBatchPayment.items, editRequestField, request.companyCurrency, sourceCurrencies]
    );

    const addBills = useCallback(
        (selectableBills: domain.AirwallexBatchSelectableBillItem[]) => {
            const newItems = [...billBatchPayment.items, ...selectableBills.filter((bill) => bill.selected)];

            setBillBatchPayment({
                ...billBatchPayment,
                items: newItems,
            });
            editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', newItems);
        },
        [billBatchPayment, editRequestField]
    );

    const { saveAndSubmit, isSaving } = useSaveAndSubmitMutation(
        billBatchPayment,
        request,
        setBillInvoiceRequestIdsWithExceededAmount,
        setBillInvoiceRequestIdsWithError,
        setDeletedBeneficiariesData,
        setChangedBeneficiariesData
    );

    const setBatchPaymentAndUpdateRequestDetails = useCallback(
        (
            newBatchPayment: domain.XeroAirwallexBatchPaymentDetails,
            propName: keyof domain.XeroAirwallexBatchPaymentDetails
        ) => {
            setBillBatchPayment(newBatchPayment);
            editRequestField<domain.XeroAirwallexBatchPaymentDetails>(propName, newBatchPayment[propName]);
        },
        [editRequestField]
    );

    const clearItems = useCallback(() => {
        setBillBatchPayment((prevBillBatchPayment) => ({
            ...prevBillBatchPayment,
            items: [],
        }));

        editRequestField<domain.XeroAirwallexBatchPaymentDetails>('items', []);

        if (
            openSplitView &&
            (contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment ||
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment)
        ) {
            setOpenSplitView(false);
            setContentSplitView({ mode: ContentSplitViewMode.Empty });
            setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
        }
    }, [
        contentSplitView.mode,
        editRequestField,
        openSplitView,
        setContentSplitView,
        setSplitViewGoToBarSettings,
        setOpenSplitView,
    ]);

    const updateAirwallexBillBatchPayment = useCallback((newBillBatchPayment: domain.XeroAirwallexBatchPayment) => {
        setBillBatchPayment(newBillBatchPayment);
    }, []);

    const applyChangedBeneficiaries = useCallback(
        (changedBeneficiaries: ChangedBeneficiary[]) => {
            const newItems = billBatchPayment.items.map((item) => {
                const changedBeneficiary = changedBeneficiaries.find(
                    (beneficiary) => beneficiary.id === item.beneficiary?.id
                );

                return {
                    ...item,
                    beneficiary:
                        item.beneficiary && changedBeneficiary
                            ? {
                                  ...item.beneficiary,
                                  version: changedBeneficiary.version,
                              }
                            : item.beneficiary,
                };
            });
            const newBillBitchPayment = {
                ...billBatchPayment,
                items: newItems,
            };

            setBillBatchPayment(newBillBitchPayment);

            saveAndSubmit(undefined, newBillBitchPayment);
        },
        [billBatchPayment, saveAndSubmit]
    );

    const value = useMemo(
        () => ({
            billBatchPayment,
            setBillBatchPayment: setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            setShowErrors,
            saveAndSubmit,
            clearItems,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            changePaymentMethod,
            changeBeneficiary,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            updateAirwallexBillBatchPayment,
            isSaving,
            companyId: request.companyId,
            companyCurrency: request.companyCurrency,
            changeBankAccount,
            canCreateBeneficiary,
            checkBeneficiaryResultsByInvoiceId,
            setCheckBeneficiaryResultsByInvoiceId,
            applyChangedBeneficiaries,
            deletedBeneficiariesData,
            setDeletedBeneficiariesData,
            changedBeneficiariesData,
            setChangedBeneficiariesData,
            editMode,
            removeLinesDisabled,
        }),
        [
            billBatchPayment,
            setBatchPaymentAndUpdateRequestDetails,
            showErrors,
            saveAndSubmit,
            clearItems,
            addBills,
            removeSelectableBillById,
            changeBillInvoiceItemField,
            changePaymentMethod,
            changeBeneficiary,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            updateAirwallexBillBatchPayment,
            isSaving,
            request.companyId,
            request.companyCurrency,
            canCreateBeneficiary,
            changeBankAccount,
            checkBeneficiaryResultsByInvoiceId,
            applyChangedBeneficiaries,
            deletedBeneficiariesData,
            changedBeneficiariesData,
            editMode,
            removeLinesDisabled,
        ]
    );

    return (
        <XeroAirwallexBatchPaymentFormContext.Provider value={value}>
            {children}
        </XeroAirwallexBatchPaymentFormContext.Provider>
    );
};

export function useXeroAirwallexBatchPaymentFormContext() {
    return useContext(XeroAirwallexBatchPaymentFormContext);
}
